import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { getTermValue, TJsonQuery } from '../../../../../packages/JsonQuery';
import { fetchHybridSearchData } from '../../../../common/actions/fetchHybridSearchData';
import { selectMlSearchSessionGuid } from '../../../../common/selectors/analytics';
import { selectIsDesktopListingVisible } from '../../../../common/selectors/desktopListing';
import { selectShouldKeepSsrListing } from '../../../../common/selectors/hybridSearch';
import { selectMaxOffersCount } from '../../../../common/selectors/listingOffers/selectMaxOffersCount';
import { selectQueryStringWithQMark } from '../../../../common/selectors/queryString';
import { selectResultsJsonQuery } from '../../../../common/selectors/results';
import { IListingOffersState } from '../../../../common/types/listingOffers';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { selectMapBounds } from '../../../selectors';
import { trackPageOpen } from '../../../tracking/trackPageOpen';
import { TMapBounds } from '../../../types/mapBounds';

export function usePinsFetching(): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const store = useStore<IApplicationState>();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const { bounds, zoom } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const isFirstRunRef = React.useRef(true);
  const listingOffersState = useSelector<IApplicationState, IListingOffersState>(state => state.listingOffers);
  const queryString = useSelector(selectQueryStringWithQMark);
  const offersCount = useSelector(selectMaxOffersCount);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const isDesktopListingVisible = useSelector(selectIsDesktopListingVisible);
  const isHybridListing = !isDesktop || isDesktopListingVisible;
  const pageNumber = getTermValue('page')(jsonQuery as TJsonQuery) || 1;

  React.useEffect(() => {
    if (!bounds || !zoom || !isFirstRunRef.current) {
      return;
    }

    isFirstRunRef.current = false;

    // deviceType передаём всегда — он нужен карте (fetchMapData). На мобвебе с уже загруженным
    // SSR-листингом (SEO) дополнительно отключаем перезагрузку листинга по карте: там карта и
    // листинг не синхронизируются, избегаем двойной загрузки (см. selectShouldKeepSsrListing).
    // В остальных случаях грузить ли листинг решает cascade fetchHybridSearchData по deviceType.
    const shouldKeepSsrListing = selectShouldKeepSsrListing(store.getState(), deviceType);

    if (shouldKeepSsrListing) {
      trackPageOpen({
        offers: listingOffersState.offers,
        pageNumber,
        mlRanking: listingOffersState.mlRanking || undefined,
        jsonQuery,
        queryString,
        offersCount,
        mlSearchSessionGuid,
        isHybrid: isHybridListing,
        isDesktop,
      });
      dispatch(
        fetchHybridSearchData({
          deviceType,
          shouldLoadListingOverride: false,
          allowNonGeoFallback: false,
        }),
      );
    } else {
      dispatch(fetchHybridSearchData({ deviceType, allowNonGeoFallback: false })).then(response => {
        if (response?.listingOffers) {
          trackPageOpen({
            ...response.listingOffers,
            jsonQuery,
            pageNumber,
            isHybrid: isHybridListing,
            isDesktop,
          });
        } else {
          trackPageOpen({
            offers: [],
            mlRanking: listingOffersState.mlRanking || undefined,
            jsonQuery,
            queryString,
            mlSearchSessionGuid,
            isHybrid: isHybridListing,
            isDesktop,
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds?.[0][0], bounds?.[0][1], bounds?.[1][0], bounds?.[1][1], deviceType]);
}
